import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});

const EFFECTIVE_DATE = '23 May 2026';

function TermsPage() {
  usePageTitle('Terms of Service');

  return (
    <div className="bg-brand-cream-shadow min-h-dvh">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-12 sm:py-24">

        <header className="mb-12 border-b border-brand-sage/15 pb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Legal
          </div>
          <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tightest text-brand-ink-soft sm:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-4 text-sm text-brand-ink-soft/55">
            Effective {EFFECTIVE_DATE}
          </p>
          <p className="mt-4 text-base text-brand-ink-soft/75 leading-relaxed">
            These terms govern your use of Bookaburra. Please read them. By
            creating an account or using the platform, you agree to be bound by
            them.
          </p>
        </header>

        <div className="space-y-10">

          <Section id="the-deal" heading="1. The deal in plain English">
            <P>
              Bookaburra is online booking software for service businesses. You
              sign up, configure your services and team, and your clients can
              book appointments online. We charge a monthly subscription for paid
              features. You own your data; we store and process it on your behalf.
            </P>
          </Section>

          <Section id="who-these-apply-to" heading="2. Who these terms apply to">
            <P>
              These Terms of Service (&ldquo;Terms&rdquo;) are a contract between
              you — the person or business creating a Bookaburra account
              (&ldquo;you&rdquo;, &ldquo;operator&rdquo;) — and Bookaburra
              (legal entity registration pending; &ldquo;Bookaburra&rdquo;,
              &ldquo;we&rdquo;, &ldquo;us&rdquo;).
            </P>
            <P>
              If you sign up on behalf of a business, you confirm that you have
              authority to bind that business to these Terms.
            </P>
          </Section>

          <Section id="your-account" heading="3. Your account">
            <P>
              You must provide accurate information when you sign up and keep it
              up to date. You are responsible for keeping your login credentials
              secure and for all activity that occurs under your account. You must
              be at least 18 years old to create an account.
            </P>
            <P>
              You may invite additional team members to your account. Each team
              member must agree to these Terms before accessing the platform.
            </P>
          </Section>

          <Section id="plans-and-billing" heading="4. Plans and billing">
            <H3>Plans</H3>
            <Table
              headers={['Plan', 'Monthly price (AUD, incl. GST)', 'Bookings per month', 'SMS included']}
              rows={[
                ['Free', 'Free', 'Up to 50', 'None'],
                ['Small business', 'A$29', 'Up to 300', '200'],
                ['Large business', 'A$159', 'Up to 2,000', '1,000'],
              ]}
            />

            <H3>Billing cycle</H3>
            <P>
              Paid subscriptions are billed monthly via Stripe. Your card is
              charged on the same date each month. Prices are in Australian
              dollars and include GST.
            </P>

            <H3>Upgrades and downgrades</H3>
            <P>
              Upgrades take effect immediately and are prorated — you pay only
              the difference for the remaining days in the current billing period.
              Downgrades take effect at the end of the current billing period and
              unused days are refunded to your payment method immediately.
            </P>

            <H3>SMS credits</H3>
            <P>
              SMS credits included in your plan reset on the first day of each
              billing period and do not carry over. Top-up credits purchased
              separately never expire and carry over if you cancel and
              resubscribe.
            </P>

            <H3>Booking limits</H3>
            <P>
              If you reach your monthly booking limit, new bookings via your
              booking page are paused until the next billing period or until you
              upgrade. Existing bookings and your booking history are never
              affected.
            </P>

            <H3>Price changes</H3>
            <P>
              We will give you at least 30 days&rsquo; notice by email before
              increasing subscription prices. Your continued use after the notice
              period constitutes acceptance of the new price.
            </P>
          </Section>

          <Section id="cancellation" heading="5. Cancellation and refunds">
            <P>
              You may cancel your subscription at any time through the billing
              portal in your admin settings. When you cancel:
            </P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                Unused days on the current billing period are refunded immediately
                to your payment method
              </li>
              <li>Your account moves to the Free plan</li>
              <li>
                Your data is retained for 30 days and then permanently deleted,
                unless you request earlier deletion or resubscribe within that
                period
              </li>
            </ul>
          </Section>

          <Section id="acceptable-use" heading="6. Acceptable use">
            <P>You must not use Bookaburra to:</P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                Collect sensitive personal information through service intake
                forms — including Medicare numbers, bank account or card details,
                passport or government ID numbers, full medical histories, or
                mental health information
              </li>
              <li>
                Send unsolicited commercial messages (spam) to clients, or add
                clients to mailing lists without their consent
              </li>
              <li>
                Impersonate another person, business, or organisation
              </li>
              <li>
                Attempt to gain unauthorised access to the platform, other
                accounts, or underlying infrastructure
              </li>
              <li>
                Use the platform in a way that violates any Australian law or
                regulation, including the <em>Spam Act 2003</em>, the{' '}
                <em>Privacy Act 1988</em>, and the{' '}
                <em>Australian Consumer Law</em>
              </li>
              <li>
                Resell or sublicense access to the platform without our written
                consent
              </li>
            </ul>
            <P className="mt-4">
              We reserve the right to suspend or terminate accounts that violate
              this policy, with or without notice, depending on severity.
            </P>
          </Section>

          <Section id="your-data" heading="7. Your data">
            <P>
              You own your data. We process it solely to provide the service as
              described in our{' '}
              <a href="/privacy" className="text-brand-sage hover:underline underline-offset-2">
                Privacy Policy
              </a>
              .
            </P>
            <P>
              You are responsible for your own obligations to your clients under
              the <em>Privacy Act 1988</em> (Cth), including maintaining a
              privacy notice that describes how you use client information
              collected through Bookaburra.
            </P>
            <P>
              You can export your booking history and client data at any time
              from your admin settings. On account closure, data is permanently
              deleted within 30 days.
            </P>
          </Section>

          <Section id="ai-features" heading="8. AI features">
            <P>
              Bookaburra includes an AI-powered booking assistant (chat) and, on
              supported plans, an AI voice agent. These features are provided on
              a best-efforts basis. While we take care to make them reliable,
              AI systems can occasionally make mistakes or misunderstand requests.
            </P>
            <P>
              You should not rely on AI-generated booking confirmations as the
              sole record of a booking. Always verify important bookings in your
              admin calendar.
            </P>
          </Section>

          <Section id="availability" heading="9. Service availability">
            <P>
              We aim for high availability but do not guarantee uninterrupted
              access to the platform. We may take the platform offline for
              maintenance, security updates, or events outside our control
              (&ldquo;force majeure&rdquo;). We will give reasonable advance
              notice for planned maintenance windows.
            </P>
          </Section>

          <Section id="intellectual-property" heading="10. Intellectual property">
            <P>
              The Bookaburra platform, including its software, design, and
              content, is owned by Bookaburra and protected by Australian
              copyright law. These Terms do not transfer any intellectual property
              rights to you.
            </P>
            <P>
              Your data — including your booking history, client records, service
              configurations, and any custom notification wording you write — is
              yours. We claim no ownership over it.
            </P>
          </Section>

          <Section id="liability" heading="11. Limitation of liability">
            <P>
              To the maximum extent permitted by Australian law:
            </P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                Bookaburra is provided &ldquo;as is&rdquo; without warranty of
                any kind, express or implied
              </li>
              <li>
                We are not liable for any indirect, incidental, consequential, or
                special loss arising from your use of the platform, including lost
                revenue, lost bookings, or reputational harm
              </li>
              <li>
                Our total liability to you in any 12-month period is capped at
                the total fees you paid us during that period
              </li>
            </ul>
            <P className="mt-4">
              Nothing in these Terms excludes, restricts, or modifies any right
              or remedy you have under the Australian Consumer Law that cannot be
              excluded, restricted, or modified by agreement.
            </P>
          </Section>

          <Section id="termination" heading="12. Termination">
            <P>
              You may close your account at any time. We may suspend or terminate
              your account immediately if you materially breach these Terms,
              engage in fraudulent activity, or use the platform in a way that
              puts other users or the platform at risk. Where reasonable and
              practical, we will give you notice and an opportunity to remedy the
              breach before terminating.
            </P>
          </Section>

          <Section id="governing-law" heading="13. Governing law">
            <P>
              These Terms are governed by the laws of New South Wales, Australia.
              Any disputes arising under these Terms will be subject to the
              exclusive jurisdiction of the courts of New South Wales, unless
              another forum is required by law.
            </P>
          </Section>

          <Section id="changes" heading="14. Changes to these terms">
            <P>
              We may update these Terms from time to time. We will notify you by
              email at least 14 days before material changes take effect.
              Continued use of Bookaburra after the notice period constitutes
              acceptance of the revised Terms. The current version is always
              available at{' '}
              <a href="/terms" className="text-brand-sage hover:underline underline-offset-2">
                bookaburra.com/terms
              </a>
              .
            </P>
          </Section>

          <Section id="contact" heading="15. Contact">
            <P>
              Questions about these Terms:{' '}
              <a href="mailto:hello@bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
                hello@bookaburra.com
              </a>
            </P>
          </Section>

        </div>

        <footer className="mt-16 border-t border-brand-sage/15 pt-8 text-sm text-brand-ink-soft/50">
          <p>
            <a href="/legal" className="hover:text-brand-sage underline-offset-2 hover:underline">
              &larr; Back to Legal
            </a>
          </p>
        </footer>

      </div>
    </div>
  );
}

function Section({ id, heading, children }: { id: string; heading: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="text-xl font-bold text-brand-ink-soft mb-4">{heading}</h2>
      {children}
    </section>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-semibold text-brand-ink-soft mt-6 mb-2">{children}</h3>
  );
}

function P({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base text-brand-ink-soft/80 leading-relaxed mb-3 ${className ?? ''}`}>
      {children}
    </p>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto mt-3 mb-4">
      <table className="w-full text-sm border-collapse rounded-xl overflow-hidden">
        <thead>
          <tr className="bg-brand-cream text-left">
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 font-semibold text-brand-ink-soft/70 border-b border-brand-sage/15">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-brand-cream/50">
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-brand-sage/10 last:border-0">
              {row.map((cell, j) => (
                <td key={j} className={`px-4 py-3 text-brand-ink-soft/75 align-top ${j === 0 ? 'font-medium text-brand-ink-soft/90' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
